import {
  type AdvancedThreshold,
  type ThresholdColor,
  transformAdvancedThresholds,
  transformAdvancedThresholdsSchema,
  transformColorThresholds,
} from "@/overlay/components/CommonParts/Threshold/types";
import { type ExtendedCoordinates, type LabeledCoordinates, transformToLabeledCoordinates } from "@/types/location";
import type { DatetimesWithOffset } from "@/utility/time";
import type {
  ElementStyle,
  LocationTableGroup,
  LocationTableSchemaInput,
  LocationTableSchemaOutput,
} from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";

export type LocationGroup = {
  name?: string;
  locations: ExtendedCoordinates[];
};

export type LocationTable = DatetimesWithOffset & {
  id: number;
  tabId: number;
  advancedThreshold: AdvancedThreshold[];
  areColumnsGrouped: boolean;
  areRowsGrouped: boolean;
  groups: LocationGroup[];
  model: string;
  parameterUnit: string;
  calibrated: boolean;
  ensSelect: string;
  resolution: string;
  thresholds: ThresholdColor[];
  timeLock: boolean;
  title: string;
  titleStyle?: ElementStyle;
  isPolygon: boolean;
  showTriggered: boolean;
  apiUrlSuffix?: string;
};

export const transformToLocationGroups = (groups: LocationTableGroup[]): LocationGroup[] => {
  return groups.map((group) => {
    const locations: LabeledCoordinates[] = transformToLabeledCoordinates(group.locations);
    return {
      name: group.name || undefined,
      locations: locations,
    };
  });
};

export const transformToLocationTable = (tableSchema: LocationTableSchemaOutput): LocationTable => {
  return {
    advancedThreshold: transformAdvancedThresholds(tableSchema.advanced_thresholds),
    areColumnsGrouped: !!tableSchema.are_columns_grouped,
    areRowsGrouped: !!tableSchema.are_rows_grouped,
    datetimeFrom: tableSchema.datetime_from,
    datetimeFromOffset: tableSchema.datetime_from_offset,
    datetimeTo: tableSchema.datetime_to,
    datetimeToOffset: tableSchema.datetime_to_offset,
    groups: transformToLocationGroups(tableSchema.groups || []),
    id: tableSchema.id,
    model: tableSchema.model || "mix",
    parameterUnit: tableSchema.parameter_unit || "t_2m:C",
    calibrated: tableSchema.calibrated || false,
    ensSelect: tableSchema.ens_select || "",
    resolution: tableSchema.resolution || "PT1H",
    tabId: tableSchema.id_tab,
    thresholds: transformColorThresholds(tableSchema.thresholds),
    timeLock: !!tableSchema.time_lock,
    title: tableSchema.title || "Title",
    titleStyle: tableSchema.titleStyle || undefined,
    isPolygon: !!tableSchema.is_polygon,
    showTriggered: !!tableSchema.show_triggered,
    apiUrlSuffix:
      typeof (tableSchema as { api_url_suffix?: string | null }).api_url_suffix === "string"
        ? (tableSchema as { api_url_suffix?: string | null }).api_url_suffix || undefined
        : undefined,
  };
};

export const transformToLocationTableInput = (table: LocationTable): LocationTableSchemaInput => {
  return {
    time_created: DateTime.now().toISO(), // TODO *!@# strip these from schema
    time_updated: DateTime.now().toISO(),
    advanced_thresholds: transformAdvancedThresholdsSchema(table.advancedThreshold),
    are_columns_grouped: table.areColumnsGrouped,
    are_rows_grouped: table.areRowsGrouped,
    datetime_from: table.datetimeFrom,
    datetime_from_offset: table.datetimeFromOffset,
    datetime_to: table.datetimeTo,
    datetime_to_offset: table.datetimeToOffset,
    groups:
      table.groups?.map((group) => {
        return {
          name: group.name || undefined,
          locations: transformToLabeledCoordinates(group.locations),
        };
      }) || [],
    id: table.id,
    id_tab: table.tabId,
    model: table.model,
    parameter_unit: table.parameterUnit,
    calibrated: table.calibrated,
    ens_select: table.ensSelect,
    resolution: table.resolution,
    thresholds: transformColorThresholds(table.thresholds),
    time_lock: table.timeLock,
    title: table.title,
    titleStyle: table.titleStyle || null,
    is_polygon: table.isPolygon,
    show_triggered: table.showTriggered,
    api_url_suffix: table.apiUrlSuffix || undefined,
  } as LocationTableSchemaInput;
};
